/**
 * Route constants for centralized route management
 * This file contains all application routes to ensure consistency
 * and make route management easier across the application.
 */

// Public routes (unauthenticated)
export const PUBLIC_ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  PRICING: '/pricing',
  ABOUT: '/about',
  CONTACT: '/contact',
  FAQ: '/faq',
  HELP: '/help',
  MOBILE_APP: '/mobile-app',
  PRIVACY: '/privacy',
  TERMS: '/terms',
  ACCOUNT_DELETED: '/account-deleted',
  /** Where an email change's links land: approve, then confirm. */
  EMAIL_CHANGE: '/email-change',
  VERIFY_EMAIL: '/verify-email',
} as const;

/** Any public (signed-out) route's path. */
export type PublicRoutePath =
  (typeof PUBLIC_ROUTES)[keyof typeof PUBLIC_ROUTES];

// Protected routes (authenticated)
export const PROTECTED_ROUTES = {
  DASHBOARD: '/dashboard',
  REPORTS: '/reports',
  CALENDAR: '/calendar',
  NOTIFICATIONS: '/notifications',
  SETTINGS: '/settings',
  /**
   * Managing who holds access to your training.
   *
   * Reached from the profile dropdown, not the sidebar: the sidebar's main
   * band is a training band and this is an account destination, alongside
   * View Profile and Settings.
   */
  FRIENDS: '/friends',
  /**
   * What your friends have been doing.
   *
   * A dashboard SUBPAGE, not a top-level destination: its only entry point is
   * the Friend Activity module on the dashboard's Social profile, which is why
   * it takes a breadcrumb naming that parent and has no sidebar slot. The
   * sidebar's main band is a training band, and this is a spectating surface.
   */
  FEED: '/feed',
  /**
   * The client's trainer directory — mirror of the trainer's `/clients`.
   *
   * Client-role only, and gated at the entry point rather than the route:
   * `GET /api/trainer` returns `[]` for anyone else. Lives beside Friends in
   * the profile dropdown for the same reason — the sidebar's main band is a
   * training band, and this is an account destination.
   */
  TRAINERS: '/trainers',
  /**
   * Where your finished week placed you, among friends and among everyone.
   *
   * Singular. The panel that leads here reads "Your Standing", and that is the
   * surface a member actually clicks — a plural board name would rename the
   * thing between the click and the arrival.
   *
   * A dashboard SUBPAGE for the same reason the feed is: its only entry point
   * is the dashboard's Social profile, so it takes a breadcrumb naming that
   * parent and has no sidebar slot. The sidebar's main band is a training band;
   * this is a spectating surface.
   */
  STANDING: '/standing',
} as const;

// Plans nested routes
export const PLANS_ROUTES = {
  LIST: '/plans',
  VIEW: '/plan/:id',
  NEW: '/plan/new',
  EDIT: '/plan/:id/edit',
  /**
   * A trainer proposing a change to a client's plan.
   *
   * The client is a path segment rather than a query because the proposal
   * cannot be sent without one, and this surface has to survive a reload —
   * `PlanPolicy` lets a trainer open a plan they authored, so the URL is
   * shareable between their own devices.
   */
  PROPOSE: '/plan/:id/propose/:username',
  SCHEDULE: '/plan/:id/schedule',
  /**
   * A trainer proposing which days a client's plan asks for.
   *
   * The schedule half of {@link PLANS_ROUTES.PROPOSE}, and a sibling of it
   * rather than a mode inside it: the two write to different endpoints and are
   * mutually exclusive as proposals, so a coach picks one and the other stays
   * one click away.
   */
  SCHEDULE_PROPOSE: '/plan/:id/schedule/propose/:username',
  WORKOUTS: '/plan/:id/workouts',
  SHARE: '/plan/:id/share',
} as const;

// Workouts nested routes
export const WORKOUTS_ROUTES = {
  LIST: '/workouts',
  VIEW: '/workout/:id',
  NEW: '/workout/new',
  EDIT: '/workout/:id/edit',
  SESSION: '/workout/:id/session',
} as const;

// Exercises nested routes
export const EXERCISES_ROUTES = {
  LIST: '/exercises',
  VIEW: '/exercise/:id',
  NEW: '/exercise/new',
  EDIT: '/exercise/:id/edit',
} as const;

// Challenges nested routes
export const CHALLENGES_ROUTES = {
  LIST: '/challenges',
  VIEW: '/challenge/:id',
  NEW: '/challenge/new',
  EDIT: '/challenge/:id/edit',
} as const;

// Goals nested routes
export const GOALS_ROUTES = {
  LIST: '/goals',
  VIEW: '/goal/:id',
  NEW: '/goal/new',
  EDIT: '/goal/:id/edit',
} as const;

/**
 * A COMPLETED session — its own noun, not a workout.
 *
 * Deliberately not `/workout/:id/summary`: every `/workout/:id/*` route takes a
 * workout-TEMPLATE hashid, and this takes a completed-workout hashid. The same
 * URL shape holding two different entities would have quietly mis-resolved in
 * `generatePath` call sites and the global-search route map. A completed
 * session also has its own policy, its own notes thread and its own lifecycle.
 */
export const SESSIONS_ROUTES = {
  VIEW: '/session/:id',
  /**
   * The full reading behind the summary's verdict card. Owner-only — narrower
   * than VIEW, which the plan's trainer may also open — because every figure
   * is localised to the authenticated viewer's units.
   *
   * The session to read against rides in `?against=<hashid>` rather than the
   * path: it maps 1:1 onto the endpoint's `compare_with`, it keeps the reading
   * shareable and refresh-proof, and it is optional in a way a path segment
   * cannot be.
   */
  COMPARE: '/session/:id/compare',
} as const;

// User measurement logs nested routes
export const MEASUREMENTS_ROUTES = {
  LIST: '/measurements',
  /**
   * One metric's history. The detail entity here is the METRIC, not the entry:
   * a measurement is an append-only series, so a single entry carries almost
   * nothing the list row does not already print, while the metric across time
   * is where every question the reader arrived with is answered.
   *
   * `:metric` is a catalogue slug (`weight`, `waist_circumference`), not an id.
   */
  METRIC: '/measurements/:metric',
  NEW: '/measurement/new',
  /**
   * One entry: its photos, then that day's readings. Where an entry's
   * progress photos are seen; an entry without photos is reached from Edit or
   * a metric page, never the timeline. The static `/measurement/new` outranks
   * `:id` on its own.
   */
  ENTRY: '/measurement/:id',
  EDIT: '/measurement/:id/edit',
} as const;

// Trainer roster routes
export const CLIENTS_ROUTES = {
  LIST: '/clients',
  /**
   * One client, as their trainer. Singular `/client/:username`, matching every
   * other detail route here (`/plan/:id`, `/goal/:id`) — and precedent-setting
   * for any future client-scoped page, which hangs off this segment rather
   * than growing a second noun.
   *
   * Keyed on the handle, not an id: `username` is the only field the roster
   * guarantees is present and meaningful, and it is what every trainer
   * endpoint takes in its path.
   *
   * Deliberately not `/profile/:username`. That page stays, and answers "who
   * is this person?" — all-time and ambient, for anyone allowed to see them.
   * This one answers "what is happening between you two?", which is a
   * different question with a different audience.
   */
  VIEW: '/client/:username',
  /**
   * One of the client's measurement entries, read-only. The entry endpoint
   * takes the id alone; `:username` names whose it is, so the page can go
   * back to the client and say who can change it.
   */
  MEASUREMENT: '/client/:username/measurement/:id',
  /**
   * One of the client's body metrics over time, read-only: the trainer's view
   * of `/measurements/:metric`. The trainer's one way into a client's
   * measurements — trainers get no client health trends.
   */
  METRIC: '/client/:username/measurements/:metric',
} as const;

// A change a trainer proposed, answered by the client it is for.
export const PROPOSALS_ROUTES = {
  /**
   * Reached by id from a notification. There is deliberately no index: a
   * client answers the one change they were told about rather than browsing
   * a queue of them.
   */
  VIEW: '/proposal/:id',
} as const;

// Profile routes
export const PROFILE_ROUTES = {
  /** The authenticated user's own profile */
  SELF: '/profile',
  /** A specific user's profile by username */
  VIEW: '/profile/:username',
} as const;

// Fitness questionnaire (multi-step onboarding flow)
export const QUESTIONNAIRE_ROUTES = {
  START: '/questionnaire',
  STEP: '/questionnaire/:step',
  GOALS: '/questionnaire/goals',
  LEVEL: '/questionnaire/level',
  SETUP: '/questionnaire/setup',
  WATCH_OUTS: '/questionnaire/watch-outs',
  GENERATE: '/questionnaire/generate',
} as const;

/**
 * Settings → Account with the recovery codes open: where "View codes" goes
 * from a toast. The Account panel reads `view` once, then drops it.
 */
export const RECOVERY_CODES_VIEW = {
  param: 'view',
  value: 'recovery-codes',
} as const;

export const RECOVERY_CODES_LINK = `${PROTECTED_ROUTES.SETTINGS}?section=account&${RECOVERY_CODES_VIEW.param}=${RECOVERY_CODES_VIEW.value}`;

// All routes combined for easier access
export const ROUTES = {
  ...PUBLIC_ROUTES,
  ...PROTECTED_ROUTES,
  PLAN: PLANS_ROUTES,
  WORKOUTS: WORKOUTS_ROUTES,
  SESSIONS: SESSIONS_ROUTES,
  EXERCISES: EXERCISES_ROUTES,
  CHALLENGES: CHALLENGES_ROUTES,
  GOALS: GOALS_ROUTES,
  MEASUREMENTS: MEASUREMENTS_ROUTES,
  CLIENTS: CLIENTS_ROUTES,
  PROPOSALS: PROPOSALS_ROUTES,
  PROFILE: PROFILE_ROUTES,
  QUESTIONNAIRE: QUESTIONNAIRE_ROUTES,
} as const;

// Route groups for conditional rendering and navigation
export const ROUTE_GROUPS = {
  PUBLIC: Object.values(PUBLIC_ROUTES),
  PROTECTED: Object.values(PROTECTED_ROUTES),
  PLAN: Object.values(PLANS_ROUTES),
  WORKOUT: Object.values(WORKOUTS_ROUTES),
  SESSION: Object.values(SESSIONS_ROUTES),
  EXERCISE: Object.values(EXERCISES_ROUTES),
  CHALLENGE: Object.values(CHALLENGES_ROUTES),
  GOAL: Object.values(GOALS_ROUTES),
  MEASUREMENT: Object.values(MEASUREMENTS_ROUTES),
  PROFILE: Object.values(PROFILE_ROUTES),
  QUESTIONNAIRE: Object.values(QUESTIONNAIRE_ROUTES),
} as const;

/**
 * Strips the leading slash from an absolute route constant so it can be used
 * as a relative child path in react-router-dom's nested route config.
 *
 * Unlike a bare `.slice(1)`, this helper throws at runtime if the value does
 * not start with `/`, making misconfigured route constants immediately visible
 * instead of silently producing a wrong path.
 *
 * @example
 *   toRelativePath(PUBLIC_ROUTES.PRICING) // → "pricing"
 *   toRelativePath('pricing')             // throws – must start with "/"
 */
export const toRelativePath = (absolutePath: string): string => {
  if (!absolutePath.startsWith('/')) {
    throw new Error(
      `toRelativePath: expected an absolute path starting with "/" but received "${absolutePath}". ` +
        `Update the route constant or pass the relative path directly.`
    );
  }
  return absolutePath.slice(1);
};

// Helper function to check if a route is protected
export const isProtectedRoute = (pathname: string): boolean => {
  return ROUTE_GROUPS.PROTECTED.some((route) => pathname.startsWith(route));
};

// Helper function to check if a route is public
export const isPublicRoute = (pathname: string): boolean => {
  return ROUTE_GROUPS.PUBLIC.includes(pathname as PublicRoutePath);
};

/**
 * Check if a pathname is a dashboard route.
 * Dashboard routes include:
 * - '/' (home route redirects to dashboard)
 * - '/dashboard' (main dashboard)
 * - '/dashboard/*' (any nested dashboard routes)
 */
export const isDashboardRoute = (pathname: string): boolean => {
  return (
    pathname === '/' ||
    pathname === '/dashboard' ||
    pathname.startsWith('/dashboard/')
  );
};
